/* Generated by uc-options-engine/tools/build-theme-cart-js.js — do not edit. */
(function () {
  "use strict";
// @ts-check

/**
 * Cart integrity for configured products.
 *
 * Follows the Auto-Gift Engine's shape deliberately (assets/custom.js): an
 * idempotent reconcile that computes what the cart SHOULD contain, diffs it
 * against what it HAS, and applies the difference. `desired === actual` being a
 * no-op is what stops cart:change -> mutate -> cart:change looping, and
 * MAX_RECONCILE_PASSES is the backstop against a rule bug spinning the browser.
 *
 * Three things can go wrong between add-to-cart and checkout, and all three
 * cost real money:
 *
 *   1. The customer removes a configured product. Its charge and add-on lines
 *      survive, and they pay for options on a product they no longer own.
 *   2. The customer raises the quantity to 2. One charge covers two products,
 *      so half the customisation is free.
 *   3. A charge line survives with no parent at all — same as (1) but arrived
 *      at by a different route, e.g. an app or another tab.
 *
 * Reconcile fixes all three by treating the PRODUCT lines as the truth and
 * making every other line in the group agree with them.
 *
 * Pure: the cart API is injected, so the whole thing runs against a simulator
 * in tests rather than needing a storefront.
 */

const GROUP = "_uc_group";
const ROLE = "_uc_role";
const FOR = "_uc_for";
const FROM = "_uc_from";
/** UncoverInsights reads this name to attribute a charge to its parent. */
const INSIGHTS_ID = "_cl_options_id";

const MAX_RECONCILE_PASSES = 5;

/**
 * @param {{cart: any, chargeVariantFor?: (amount:number)=>string|null, log?: Function}} deps
 */
function createCartSync({ cart, log = () => {} }) {

  /** Group the cart's lines by our token. Lines without one are not ours. */
  function groupLines(lines) {
    const groups = new Map();
    for (const line of lines) {
      const token = line.properties?.[GROUP];
      if (!token) continue;
      const role = line.properties?.[ROLE];
      if (!role) continue;

      let group = groups.get(token);
      if (!group) {
        group = { token, products: [], charges: [], addons: [] };
        groups.set(token, group);
      }
      if (role === "product") group.products.push(line);
      else if (role === "charge") group.charges.push(line);
      else if (role === "addon") group.addons.push(line);
    }
    return groups;
  }

  /**
   * What each non-product line's quantity should be, given its parent.
   * Returns a list of {key, quantity} changes; quantity 0 means remove.
   */
  function plan(groups) {
    const changes = [];

    for (const group of groups.values()) {
      const parent = group.products[0];

      // No parent: every remaining line in the group is an orphan. This is the
      // leak that matters most — the customer is being charged for options on
      // a product that is no longer in their cart.
      if (!parent) {
        for (const line of [...group.charges, ...group.addons]) {
          changes.push({ key: line.key, quantity: 0, why: "orphan" });
        }
        continue;
      }

      // Two product lines sharing a token means the token was reused. Touching
      // anything here risks charging the wrong line, so leave the group alone
      // and say so — an untidy cart beats a mispriced one.
      if (group.products.length > 1) {
        log("duplicate token, leaving group alone", group.token);
        continue;
      }

      const parentQty = parent.quantity;
      if (!Number.isInteger(parentQty) || parentQty < 1) continue;

      // One charge line per priced option, each covering one unit of the
      // parent. Two of the product means twice the customisation.
      for (const line of group.charges) {
        if (line.quantity !== parentQty) {
          changes.push({ key: line.key, quantity: parentQty, why: "charge qty" });
        }
      }

      // Add-ons carry their own per-unit multiple in _uc_per, set when added.
      for (const line of group.addons) {
        const per = Number(line.properties?.["_uc_per"]) || 1;
        const want = per * parentQty;
        if (line.quantity !== want) {
          changes.push({ key: line.key, quantity: want, why: "addon qty" });
        }
      }
    }

    return changes;
  }

  /**
   * `mutated` means a change was APPLIED, not merely attempted. The caller
   * repaints on it, and on the cart page repainting is a reload — so a change
   * Shopify keeps refusing must not read as success, or every load would
   * reload again.
   *
   * @returns {Promise<{mutated:boolean, changes:any[]}>}
   */
  async function reconcile(pass = 0) {
    if (pass >= MAX_RECONCILE_PASSES) {
      log("pass cap reached, giving up");
      return { mutated: false, changes: [] };
    }

    const current = await cart.get();
    const changes = plan(groupLines(current.items || []));
    if (changes.length === 0) return { mutated: false, changes: [] };

    let applied = 0;
    for (const change of changes) {
      // Keys are re-read from a fresh cart on every pass, because changing one
      // line can renumber the others.
      const ok = await cart.change({ id: change.key, quantity: change.quantity });
      if (ok) applied += 1;
      else log("could not apply", change);
    }

    const next = await reconcile(pass + 1);
    return { mutated: applied > 0 || next.mutated, changes: changes.concat(next.changes) };
  }

  /**
   * Add a configured product and everything that belongs with it, in ONE
   * request. A partial add — product on, charge missing — is an underpriced
   * order, so it must not be possible for the first call to succeed and the
   * second to fail.
   */
  async function addConfigured({ lines }) {
    if (!lines?.length) throw new Error("nothing to add");
    const ok = await cart.add(lines.map(toCartItem));
    return ok;
  }

  return { reconcile, addConfigured, plan, groupLines };
}

function toCartItem(line) {
  return { id: line.id, quantity: line.quantity, properties: line.properties };
}

// @ts-check

/**
 * Wires the engine into the Impact theme. The only file that knows anything
 * about Shopify or this particular theme; everything below it is portable.
 *
 * Three integration points, each with a reason it works the way it does:
 *
 *   1. ADD TO CART is intercepted rather than augmented. A configured product
 *      is several lines — the product, one charge per paid option, any add-ons
 *      — and a product form can only express one. Two sequential requests could
 *      half-succeed and leave a product with no charge line, which is an
 *      underpriced order, so all lines go in ONE POST.
 *
 *   2. REPAINT is `cart:refresh`, not `cart:change`. The drawer paints HTML
 *      captured from the /cart/add.js response, rendered BEFORE our lines
 *      existed — so cart:change alone shows a drawer missing the charge.
 *      Documented the hard way by the Auto-Gift Engine; see assets/custom.js.
 *
 *   3. VARIANT CHANGE re-resolves. 23 conditions in the live ruleset key on
 *      variant_title — "no cum tube on X-Small" and similar — so switching size
 *      can legitimately add or remove options.
 */

const ROOT = "[data-uc-options-root]";

/**
 * @param {object} [options]
 * @param {Document} [options.doc]
 * @param {any} [options.win]
 * @param {string} [options.routes]
 * @param {(...args:any[])=>void} [options.log]
 * @param {Document|Element} [options.scope]  where this instance's markup lives
 * @param {boolean} [options.cart]            boot the shop-wide cart work too
 */
function boot({
  doc = document,
  win = window,
  routes = win.Shopify?.routes?.root || "/",
  log = (...args) => console.warn("[options]", ...args),
  /*
   * WHERE THIS INSTANCE'S MARKUP LIVES, and the reason there can be two.
   *
   * The quick-view drawer fetches another product's page and injects its
   * `#main` into this document — a second options root, a second add-to-cart
   * form, a second everything, all of it belonging to a different product than
   * the page around it. One instance cannot serve both: `product` is captured
   * once, and the submit interceptor has to be able to say which form is whose.
   *
   * So the drawer gets an instance of its own, scoped to the injected element.
   * Everything that used to be looked up on the document is looked up here
   * instead, which is what keeps the page's engine and the drawer's from
   * answering for each other's product.
   */
  scope = doc,
  /*
   * The cart work is shop-wide and idempotent, and the drawer is not a new
   * shop. `bootCart` guards itself, but saying so at the call site is what
   * stops a reader wondering whether opening a drawer re-reconciles the cart.
   */
  cart = true,
} = {}) {
  /*
   * WHICH DRAWER, IF ANY, THIS INSTANCE LIVES IN. Everything below asks the
   * same question of every element it is handed: is the nearest drawer around
   * you the one around me? The page is inside no drawer, so `null` is a real
   * answer and not an absence.
   */
  const scopeDrawer = scope.closest?.("#uc-drawer-content") || null;
  const belongsHere = (node) =>
    (node?.closest?.("#uc-drawer-content") || null) === scopeDrawer;

  /*
   * THE FIRST ROOT IS NOT NECESSARILY THIS INSTANCE'S.
   *
   * A quick view injects a whole product page into this document, root and
   * all. So a document-scoped boot that took `querySelector` at its word would
   * pick up the DRAWER's root whenever the page had none of its own — which is
   * every collection page, where the engine now loads on demand precisely
   * because a drawer opened. It would render the drawer's options and then
   * decline the drawer's form, having decided it was foreign.
   */
  const root = [...scope.querySelectorAll(ROOT)].find(belongsHere) || null;
  const rules = win.UC_OPTIONS_RULES;
  /*
   * READ AT BOOT, and for the drawer that timing is the whole mechanism: the
   * injected page carries its own `<script>` setting these globals, `custom.js`
   * re-executes it, and only then is this called. So the drawer instance reads
   * the drawer product's payload from the same place the page read its own.
   *
   * It also means the globals now describe the LAST thing rendered rather than
   * the page. Nothing may read them after boot for that reason — everything
   * below closes over these constants instead.
   */
  const product = win.UC_OPTIONS_PRODUCT;

  /*
   * THE CART WORK RUNS EVERYWHERE; THE OPTIONS WORK ONLY WHERE THERE ARE OPTIONS.
   *
   * Both used to live below this early return, which meant a page with no
   * options got neither — and two things quietly depended on that being wrong.
   * The header count showed the raw item total on the cart page, on collections
   * and on the home page, so one configured dildo read as "2" everywhere except
   * the product page itself. And cart integrity only ran on product pages, so
   * removing the product from the CART page left its Customisation charge
   * behind, payable, with nothing attached to it.
   */
  if (cart) bootCart({ doc, win, routes, log });

  // Nothing more to do on a product with no options configured. Not an error.
  if (!root || !rules || !product) return null;

  /*
   * One signal for every listener this instance adds, so the drawer's can all
   * be dropped at once when it closes. Without it each quick view leaves a
   * document-level capture listener behind holding the previous product, and
   * the fifth one wins the next add to cart.
   */
  const listeners = new AbortController();
  const on = (target, type, handler, opts) =>
    target.addEventListener(type, handler, { ...(opts || {}), signal: listeners.signal });

  const chargePrices = win.UC_OPTIONS_CHARGE_PRICES || {};
  const addonPrices = win.UC_OPTIONS_ADDON_PRICES || {};
  /* Only the sold-out ids are listed; absence means available, so a snippet
   * that failed to render cannot take a whole range off sale. */
  const soldOut = win.UC_OPTIONS_ADDON_SOLD_OUT || [];
  /* Each linked product's own picture, so an option showing a product shows
   * that product rather than a URL copied out of the old app. */
  const addonImages = win.UC_OPTIONS_ADDON_IMAGES || {};
  /*
   * The colour guide's own photograph of each pour, keyed by the frozen colour
   * NAME — the only thing the browser knows a value by, the same reasoning that
   * makes `apply` translate keys to names before it ships.
   *
   * Emitted by uc-options-colour-images.liquid, which resolves the registry's
   * filenames through this store's Files. Absent, every colour falls back to
   * its hex exactly as before, so a store that has not uploaded the guide
   * photographs (Sinnovator today) loses nothing.
   */
  const colourImages = win.UC_OPTIONS_COLOUR_IMAGES || {};
  const variantPrices = product.variantPrices || {};
  if (Object.keys(chargePrices).length === 0) {
    // Options will render, priced silently. Better than a wrong price, but it
    // means the charge collection is missing — see uc-options-charge-prices.liquid.
    log("no charge prices available; options will show without prices");
  }

  const money = makeMoneyFormatter(win);
  const chargeVariantFor = win.UCOptions.makeChargeLookup({ variants: rules.charge || {} });

  const ui = win.UCOptions.mount({
    root,
    rules,
    product,
    variant: product.variant || {},
    chargePrices,
    addonPrices,
    addonImages,
    colourImages,
    soldOut,
    basePrice: product.variant?.price ?? 0,
    formatMoney: money,
    chargeVariantFor,
    onChange: (state) => {
      // Let the theme's own price block show the configured total.
      doc.dispatchEvent(new CustomEvent("uc-options:change", { detail: state, bubbles: true }));
    },
  });

  // mount() fires onChange synchronously, before anything registered in this
  // same tick can be listening. Re-announce once the tick drains so the
  // theme's price block gets initial state without having to poll ui.state.
  setTimeout(() => {
    doc.dispatchEvent(new CustomEvent("uc-options:change", { detail: ui.state, bubbles: true }));
  }, 0);

  /* ── Add to cart ─────────────────────────────────────────────────── */

  /*
   * Bound on the DOCUMENT in the capture phase, not on the form.
   *
   * Impact's form is a customized built-in (`<form is="product-form">`), so its
   * own submit listener is attached while the element is parsed — before this
   * deferred script runs. Listeners on the TARGET element fire in registration
   * order whatever their capture flag, so binding to the form would run second,
   * after the theme had already added the product with none of our properties.
   *
   * Capture on an ancestor genuinely precedes the target, which is the only
   * placement that lets us take over the submit.
   */
  /**
   * EVERY LINE-ITEM PROPERTY THE FORM WAS CARRYING.
   *
   * Taking over the submit means the form is never posted, so anything in it
   * that is not `id` or `quantity` was simply dropped — silently, and only on
   * products where something else put a property there.
   *
   * That cost order #40359 a ready-made's stock code and its firmness: the page
   * emitted both as hidden inputs, the engine posted its own line without them,
   * and PourHub booked a shelf item as one to manufacture.
   *
   * `form.elements` rather than `querySelectorAll`, because a field may be
   * associated with the form by its `form=` attribute while sitting outside it
   * in the DOM — which is exactly how these are rendered.
   */
  function formProperties(form, view) {
    const out = {};
    const fields = form.elements ? [...form.elements] : [...form.querySelectorAll("[name]")];
    for (const field of fields) {
      const name = field?.getAttribute?.("name") || field?.name;
      if (!name || !name.startsWith("properties[") || !name.endsWith("]")) continue;
      if (field.disabled) continue;
      // An unticked checkbox is a property the customer declined, not an empty one.
      if ((field.type === "checkbox" || field.type === "radio") && !field.checked) continue;
      const key = name.slice("properties[".length, -1);
      if (key) out[key] = field.value ?? "";
    }
    return out;
  }

  /**
   * IS THIS FORM THE ONE THIS PAGE'S OPTIONS BELONG TO?
   *
   * The listener is on the DOCUMENT in capture phase, because that is the only
   * placement that beats Impact's own product-form (see the note above). The
   * cost is that it sees EVERY add-to-cart form on the page, and a product page
   * has at least four:
   *
   *   snippets/product-info.liquid              complementary products, on the PDP itself
   *   sections/cart-drawer.liquid               cart drawer upsells
   *   sections/related-products.liquid          quick-buy on a product card
   *   sections/recently-viewed-products.liquid  the same
   *
   * Without this check every one of them was taken over as if it were the
   * configured product: the variant id came from the submitted form while the
   * options, the charge and the "For:" label came from the PDP. Seven orders
   * between 7 and 9 Sep 2026 carried a second Customisation charge for a choice
   * the customer made once. #40422 paid 11.07 "For: The Handheld Tentacle
   * Grinder II" - a product that was not in the order at all.
   *
   * TWO INDEPENDENT CHECKS, because they fail in different directions.
   *
   * The variant list is exact, but it comes from the page (uc-options.liquid
   * emits variantPrices for every variant). If it were ever missing we must NOT
   * conclude "not ours" - that hands the real product back to the theme, which
   * adds it with no charge line, and a silently underpriced order is worse than
   * a refundable double charge. An absent list abstains rather than rejects.
   *
   * The ancestry check does not depend on the page payload at all, and it
   * catches what the variant list cannot see: the SAME product quick-buyable
   * from a card, or the quick-view drawer, which injects another PDP's #main
   * into this very document.
   */
  const FOREIGN_FORM_ANCESTORS =
    ".product-card, .horizontal-product, cart-drawer, quick-buy-drawer";

  /*
   * THE DRAWER IS FOREIGN TO THE PAGE AND NATIVE TO ITSELF.
   *
   * `#uc-drawer-content` used to be in the list above, flatly. That was right
   * while the page was the only instance — the drawer held another product's
   * form and taking it over is precisely the September double-charge. It is
   * wrong now that the drawer has an instance of its own, which must own that
   * same form and nothing else.
   *
   * So the test is relative: a form is this instance's only if the nearest
   * drawer around it is the one this instance is scoped to. The page (scoped to
   * the document) is inside no drawer, so any form that is rejects — the old
   * behaviour exactly. The drawer instance accepts only forms inside itself.
   *
   * Both instances still apply the card/upsell check above, because the drawer
   * has upsells of its own.
   */
  function ownsForm(form, submittedVariantId) {
    if (form.closest?.(FOREIGN_FORM_ANCESTORS)) return false;
    if (!inScope(form)) return false;
    const known = Object.keys(product.variantPrices || {});
    if (!submittedVariantId || known.length === 0) return true;
    return known.includes(String(submittedVariantId));
  }

  /*
   * Does this element belong to THIS instance rather than the other one?
   *
   * Every listener below is still bound to the document — the submit one has
   * to be, for the capture-phase reason above, and the rest follow it so there
   * is one rule rather than two. Which means both instances see every event,
   * and each has to recognise its own. Sharing one test keeps the sticky bar,
   * the submit interceptor and the variant watcher from ever disagreeing about
   * which product a click belongs to.
   */
  const inScope = belongsHere;

  on(doc, "submit", async (event) => {
    const form = event.target;
    if (!(form instanceof win.HTMLFormElement)) return;
    if (!/\/cart\/add/.test(form.getAttribute("action") || "")) return;

    const submittedVariantId = form.querySelector('[name="id"]')?.value;

    if (ownsForm(form, submittedVariantId)) {
      event.preventDefault();
      event.stopPropagation();

      /* Everything that can start an add shows that it started one. The form's
       * own submit button is the theme's; [data-uc-add] is the sticky bar's,
       * which sits outside the form and would otherwise look inert for the
       * whole request — the customer taps it, nothing visibly happens, and
       * they tap again. Both are `is="custom-button"`, so aria-busy is all
       * either needs to run the theme's three-dot animation. */
      const busyButtons = [
        form.querySelector('[type="submit"]'),
        ...scope.querySelectorAll("[data-uc-add]"),
      ].filter(Boolean);
      const variantId = submittedVariantId || product.variant?.id;
      const quantity = Number(form.querySelector('[name="quantity"]')?.value) || 1;

      let lines;
      try {
        lines = ui.cartLines({
          variantId, quantity, formProperties: formProperties(form, win),
        }).lines;
      } catch (error) {
        // A required option is unanswered. Say so and stay put rather than
        // adding a half-configured product.
        log(error.message);
        doc.dispatchEvent(new CustomEvent("uc-options:invalid", {
          detail: { message: error.message, missing: ui.state.missingRequired }, bubbles: true,
        }));
        return;
      }

      busyButtons.forEach((el) => el.setAttribute("aria-busy", "true"));
      try {
        /*
         * WHY THIS MIRRORS IMPACT'S OWN FORM STEP FOR STEP.
         *
         * Taking over the submit meant the theme's ProductForm never ran, and
         * with it went the thing that opens the cart drawer: it is not the add
         * request that opens it but a `variant:add` event carrying a
         * server-rendered `variant-added` section. We dispatched `cart:refresh`
         * instead, which re-renders a drawer nobody opened — so the product
         * went into the cart silently and the customer had no idea whether it
         * had worked.
         *
         * `cart:prepare-bundled-sections` asks the rest of the theme which
         * sections it needs rendered with the add, rather than assuming it is
         * only ever `variant-added`; anything else listening gets its say too.
         */
        const sectionsToBundle = ["variant-added"];
        doc.documentElement?.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", {
          bubbles: true, detail: { sections: sectionsToBundle },
        }));

        const response = await fetch(`${routes}cart/add.js`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            // Without this Shopify skips the inventory check and will happily
            // add an out-of-stock linked product.
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify({
            items: lines,
            /* One request adds the product, the charge and every linked
             * product, so the section rendered from it already shows all of
             * them — which is what note 2 above was working around. */
            sections: sectionsToBundle.join(","),
            sections_url: `${routes}variants/${variantId}`,
          }),
        });

        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          log("add to cart failed:", body?.description || response.status);
          doc.dispatchEvent(new CustomEvent("uc-options:invalid", {
            detail: { message: body?.description || "Could not add to cart" }, bubbles: true,
          }));
          return;
        }

        /* A page cart has no drawer to open. */
        if (win.themeVariables?.settings?.cartType === "page" ||
            win.themeVariables?.settings?.pageType === "cart") {
          win.location.href = `${routes}cart`;
          return;
        }

        const added = await response.json().catch(() => ({}));
        const cart = await fetch(`${routes}cart.js`).then((r) => r.json()).catch(() => null);
        if (!cart) {
          doc.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
          return;
        }
        cart.sections = added.sections;

        /* From the FORM, bubbling — quick-buy drawers listen on the form they
         * belong to, while the cart drawer listens on the document. */
        form.dispatchEvent(new CustomEvent("variant:add", {
          bubbles: true,
          detail: { items: added.items || [added], cart },
        }));
        doc.documentElement?.dispatchEvent(new CustomEvent("cart:change", {
          bubbles: true, detail: { baseEvent: "variant:add", cart },
        }));
      } finally {
        busyButtons.forEach((el) => el.removeAttribute("aria-busy"));
      }
    }
  }, { capture: true });   // capture on document — see the note above

  /* The sticky bar's own button. It submits the theme's form rather than
   * posting on its own, so validation, selling plans and everything else the
   * form carries keep working — and there is exactly one add-to-cart path to
   * reason about. */
  on(doc, "click", (event) => {
    const trigger = event.target.closest?.("[data-uc-add]");
    if (!trigger || trigger.disabled) return;
    /* The page's sticky bar and the drawer's are both on screen at once while a
     * quick view is open, and both instances hear both clicks. Without this the
     * drawer's engine answered the page's button and added the drawer's
     * product alongside it. */
    if (!inScope(trigger)) return;

    /* The FIRST /cart/add form on the page is not necessarily this product's.
     * A product page carries several — the complementary-products block, cart
     * drawer upsells, quick-buy on related and recently-viewed cards — and
     * which one comes first in the DOM changes as those regions populate. That
     * is why adding a second size failed: with something already in the cart
     * the drawer had a form of its own, this picked it up, and the sticky bar
     * submitted a form belonging to a different product.
     *
     * ownsForm is the same test the submit interceptor uses, so the button and
     * the interceptor cannot disagree about which form is ours. */
    const form = Array.from(doc.querySelectorAll('form[action*="/cart/add"]'))
      .find((candidate) =>
        ownsForm(candidate, candidate.querySelector('[name="id"]')?.value));

    if (!form) return;
    event.preventDefault();
    if (typeof form.requestSubmit === "function") form.requestSubmit();
    else form.dispatchEvent(new CustomEvent("submit", { bubbles: true, cancelable: true }));
  });

  /* ── Variant changes ─────────────────────────────────────────────── */

  on(doc, "variant:change", (event) => {
    const variant = event.detail?.variant;
    if (!variant) return;
    /* Bubbles to the document, so the page hears the drawer's size change and
     * the drawer hears the page's. Each re-resolves its own product only. */
    if (!inScope(event.target)) return;
    /* The price moves with the size, so the running total has to move with it.
     * Shopify's own event carries a price; the map is the fallback for themes
     * that dispatch only an id. */
    const basePrice = typeof variant.price === "number"
      ? variant.price
      : variantPrices[String(variant.id)];
    ui.refresh({ variant: { id: variant.id, title: variant.title }, basePrice });
  });

  /*
   * The drawer is torn down and rebuilt on every open, so its instance has to
   * be able to go away. `ui.destroy` releases the rendered UI; the controller
   * drops every listener this instance put on the document.
   */
  /* Captured BEFORE the assign below replaces it. Reading `ui.destroy` inside
   * the new one calls the new one — infinite recursion, and only on teardown,
   * which is the path nothing exercises by hand. */
  const destroyUi = ui.destroy?.bind(ui);
  return Object.assign(ui, {
    destroy() {
      listeners.abort();
      destroyUi?.();
    },
  });
}

/**
 * Everything about the cart that is true on every page of the shop.
 *
 * Kept apart from the options themselves because it has nothing to do with
 * them: a customer on the cart page has no options on screen and still has a
 * header count to keep honest and orphaned charge lines to be rid of.
 */
function bootCart({ doc, win, routes, log }) {
  /*
   * Idempotent, because there are two ways in. On a product page the cart
   * bundle boots this and then the options bundle loads and boots too; on every
   * other page only the cart bundle runs. Two sets of listeners would reconcile
   * the cart twice on every change and race each other doing it.
   */
  if (win.__ucCartBooted) return;
  win.__ucCartBooted = true;

  /* ── The cart bubble counts what is being shipped ─────────────────
   *
   * A configured product is two or three cart lines, so `cart.item_count` — and
   * therefore the header bubble — said "2" for one dildo, with no way for the
   * customer to tell what the second thing was. Liquid renders the corrected
   * number (snippets/uc-cart-count.liquid), but the theme's <cart-count>
   * recomputes from the raw cart on every change and puts the wrong one back.
   *
   * Hence an observer rather than another event listener: it catches every path
   * that writes to the element, including the ones we have not gone looking
   * for, and it cannot race the theme's own async update. Writing only when the
   * value differs is what stops it chasing its own tail — the theme's setter
   * ignores an unchanged value too.
   */
  let expectedCount = null;

  const shippedCount = (cart) => (cart?.items || []).reduce(
    (total, item) => item.properties?._uc_role === "charge" ? total : total + (item.quantity || 0), 0);

  function correctCount(element) {
    if (expectedCount === null) return;
    try {
      if (element.itemCount !== expectedCount) element.itemCount = expectedCount;
    } catch {
      element.textContent = String(expectedCount);
    }
  }

  async function syncCartCount() {
    const cart = await fetch(`${routes}cart.js`).then((r) => r.json()).catch(() => null);
    if (!cart) return;
    expectedCount = shippedCount(cart);
    for (const element of doc.querySelectorAll?.("cart-count") || []) {
      if (!element.ucCountWatched && typeof win.MutationObserver === "function") {
        element.ucCountWatched = true;
        new win.MutationObserver(() => correctCount(element))
          .observe(element, { childList: true, characterData: true, subtree: true });
      }
      correctCount(element);
    }
  }

  for (const event of ["cart:change", "cart:refresh", "variant:add"]) {
    doc.addEventListener(event, () => { syncCartCount(); });
  }
  syncCartCount();

  /* ── Cart integrity ──────────────────────────────────────────────── */

  const sync = win.UCOptions.createCartSync({
    cart: {
      get: async () => (await fetch(`${routes}cart.js`)).json(),
      add: async (items) => {
        const r = await fetch(`${routes}cart/add.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
          body: JSON.stringify({ items }),
        });
        return r.ok;
      },
      change: async ({ id, quantity }) => {
        const r = await fetch(`${routes}cart/change.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id, quantity }),
        });
        return r.ok;
      },
    },
    log,
  });

  let chain = Promise.resolve();
  const reconcile = () => {
    chain = chain.then(async () => {
      const { mutated } = await sync.reconcile();
      if (!mutated) return;
      // Same repaint split the gift engine documents: cart:refresh re-renders
      // the drawer but not the cart PAGE, which reloads instead.
      if (win.themeVariables?.settings?.pageType === "cart") reloadCartPageOnce();
      else doc.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
    }).catch((error) => log(error));
    return chain;
  };

  /*
   * The reload only repaints. By the time it is called the cart itself is
   * already correct, and checkout reads the cart, not the page — so a reload
   * that is skipped costs a stale line on screen, never money. That is why a
   * missing sessionStorage means "do not reload" rather than "reload anyway":
   * the guard is what stops a load-time reconcile reloading forever if Shopify
   * ever reports a change as applied without making it.
   */
  function reloadCartPageOnce() {
    const KEY = "uc-cart-reconciled-at";
    try {
      const last = Number(win.sessionStorage.getItem(KEY)) || 0;
      if (Date.now() - last < 10000) return;
      win.sessionStorage.setItem(KEY, String(Date.now()));
    } catch {
      return;
    }
    win.location.reload();
  }

  doc.addEventListener("cart:change", reconcile);
  win.addEventListener("pageshow", (e) => { if (e.persisted) reconcile(); });

  /*
   * AND ON EVERY PAGE LOAD, because the cart page never says it changed.
   *
   * Removing a line on the cart PAGE does not dispatch cart:change: Impact's
   * `_changeLineItemQuantity` posts /cart/change.js and then reloads the page
   * when pageType is "cart". The listener above never fires, the reloaded page
   * only listened, and the product's Customisation charge and add-ons went
   * through checkout attached to nothing. Orders #40929, #41305, #41919 and
   * #4209-SIN (Sep–Oct 2026) each paid for one: a second Werewolf charge, £22.68
   * "For: The Angel Grinder" with no grinder in the order, a Suction Cup Mount
   * charge with no mount, a second Depth Trainer III charge plus its suction cup.
   *
   * The no-JS remove links (/cart/change?id=…&quantity=0) and edits made in
   * another tab arrive the same way, as a page load with a cart already wrong.
   * Reconcile is idempotent, so on a clean cart this is one GET of /cart.js.
   */
  reconcile();

}

/**
 * Formats minor units in the CUSTOMER'S currency. Intl gets the symbol,
 * grouping and decimal places right per locale, which hand-rolled
 * money_format string substitution does not.
 *
 * A WHOLE AMOUNT LOSES ITS DECIMALS, because the rest of the store does the
 * same — Impact renders line prices with `money_without_trailing_zeros`, so
 * "£39.00" beside the theme's "£39" read as a different kind of number. A price
 * with real pence keeps them: £17.95 is £17.95 everywhere.
 */
function makeMoneyFormatter(win) {
  const currency = win.UC_OPTIONS_CURRENCY || win.Shopify?.currency?.active || "GBP";
  const build = (digits) => {
    try {
      return new Intl.NumberFormat(win.Shopify?.locale || undefined, {
        style: "currency", currency,
        minimumFractionDigits: digits, maximumFractionDigits: digits,
      });
    } catch {
      return null;
    }
  };

  const withPence = build(2);
  const whole = build(0);
  if (!withPence) return (minor) => `${(minor / 100).toFixed(2)}`;

  return (minor) => (minor % 100 === 0 && whole
    ? whole.format(minor / 100)
    : withPence.format(minor / 100));
}


  window.UCOptions = window.UCOptions || {};
  window.UCOptions.createCartSync = window.UCOptions.createCartSync || createCartSync;
  window.UCOptions.bootCart = window.UCOptions.bootCart || bootCart;

  /* The cart is the same cart whether or not this page sells options. */
  function start() {
    bootCart({
      doc: document,
      win: window,
      routes: window.Shopify?.routes?.root || "/",
      log: (...args) => console.warn("[options:cart]", ...args),
    });
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }
})();
